import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';

@Injectable({ providedIn: 'root' })
export class ClientBalanceService {
  private readonly supabase = inject(SupabaseService);

  async getProviderClientBalance(): Promise<number> {
    const accessToken = await this.getAccessToken();
    const { data, error } = await this.supabase.instance.functions.invoke('client-get-provider-balance', {
      body: {},
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (error) {
      const message = await this.extractFunctionErrorMessage(error, 'No se pudo consultar saldo proveedor.');
      throw new Error(message);
    }

    const response = this.toRecord(data);
    if (response['success'] !== true) {
      throw new Error(this.toSafeString(response['error']) || 'No se pudo consultar saldo proveedor.');
    }

    const balance = Number(response['balance_sms'] ?? response['external_balance']);
    if (!Number.isFinite(balance)) {
      throw new Error('Saldo proveedor inválido.');
    }

    return Math.max(0, Math.trunc(balance));
  }

  private async getAccessToken(): Promise<string> {
    const { data: sessionData, error: sessionError } = await this.supabase.instance.auth.getSession();
    const token = sessionData.session?.access_token;

    if (sessionError || !token) {
      throw new Error('No se pudo validar la sesión.');
    }

    return token;
  }

  private async extractFunctionErrorMessage(error: unknown, fallbackMessage: string): Promise<string> {
    const fallback = error instanceof Error ? error.message : fallbackMessage;
    const context = (error as { context?: Response })?.context;

    if (!context) {
      return fallback;
    }

    try {
      const payload = await context.clone().json() as {
        error?: string;
        message?: string;
      };

      return payload.error || payload.message || fallback;
    } catch {
      return fallback;
    }
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : {};
  }

  private toSafeString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
